import { Component, type ErrorInfo, type ReactNode } from 'react'

import { messageFor } from '@/shared/api'
import { PrimaryButton, ScreenBackground } from '@/shared/ui'

/**
 * Nothing above the router catches a throw. react-router's `errorElement`
 * covers the routes it owns, and `AppConfigGate` runs before `RouterProvider`
 * mounts — so a failure while booting unmounts the whole tree and leaves the
 * page backdrop standing with nothing on it. That is what `/app/config`
 * answering 500 looked like in production: not an error screen, a blank one.
 *
 * The config call itself falls back to defaults now (`shared/api/config.ts`),
 * so what reaches here is the rest: a response the contract does not describe,
 * or a status the fallback deliberately does not swallow. This is the last net
 * — it says what happened, in Persian, rather than showing nothing at all.
 */
interface BootErrorBoundaryState {
  error: Error | null
}

export class BootErrorBoundary extends Component<
  { children: ReactNode },
  BootErrorBoundaryState
> {
  override state: BootErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): BootErrorBoundaryState {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The tree is gone by the time this renders, so the console is the only
    // place the original stack survives.
    console.error('[boot] the app failed before the router mounted', error, info)
  }

  override render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <ScreenBackground
        variant="app"
        className="flex flex-col items-center justify-center gap-4 px-8"
      >
        <h1 className="text-24 font-bold text-ink">برنامه بالا نیامد</h1>
        <p className="text-center text-15 text-ink-80">{messageFor(error)}</p>
        <div className="w-full max-w-[362px] pt-2">
          {/* The router never mounted, so a reload is the only retry there is. */}
          <PrimaryButton onClick={() => window.location.reload()}>تلاش دوباره</PrimaryButton>
        </div>
      </ScreenBackground>
    )
  }
}
